import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Loader } from "semantic-ui-react";

import { useLoginQRCode } from "modules/Login/components/QrLogin/hooks";
import "modules/Login/components/QrLogin/QrLogin.scss";

import type { UseQueryResult } from "@tanstack/react-query";
import type { TFunction } from "i18next";

const TIMEOUT = 60;

const renderLoader = (isVerified: boolean, isGenerateError: boolean, isVerifyError: boolean) => {
  return isGenerateError || isVerifyError || !isVerified ? null : <Loader active />;
};

const renderQRCode = (generateQRCode: UseQueryResult<string, Error>, isVerifyError: boolean, isExpired: boolean, isVerified: boolean) => {
  if (!generateQRCode.data || isVerified) return null;

  return (
    <QRCodeSVG
      value={generateQRCode.data}
      fgColor={isExpired || generateQRCode.isError || isVerifyError ? "#e03f3f" : "#000000"}
      style={{
        filter: isExpired || generateQRCode.isError || isVerifyError ? "blur(7px)" : ""
      }}
    />
  );
};

const renderExpiration = (
  isExpired: boolean,
  generateQRCode: UseQueryResult<string, Error>,
  isVerifyError: boolean,
  isVerified: boolean,
  secondsLeft: number,
  t: TFunction
) => {
  if (!generateQRCode.data || isVerified || generateQRCode.isError || isVerifyError) return null;

  return (
    <div>
      {isExpired ? (
        <span className="code-expired-text">
          <Icon name="info circle" /> {t("v8_code_expired", "Code expired")}
        </span>
      ) : (
        <span className="code-expiration-text">
          {t("v8_your_code_will_expire_in", "Your code will expire in")} <b>{secondsLeft} sec</b>
        </span>
      )}
    </div>
  );
};

const renderError = (isGenerateError: boolean, isVerifyError: boolean, t: TFunction) => {
  if (!isGenerateError && !isVerifyError) return null;

  return (
    <span className="code-expired-text">
      <Icon name="exclamation circle" /> {t("v8_an_error_occurred", "An error occurred")}
    </span>
  );
};

export const QRLogin = () => {
  const { generateQRCode, verifyQRCode, isExpired, isVerified, secondsLeft } = useLoginQRCode(TIMEOUT);

  const { t } = useTranslation();

  return (
    <div className="QRCodeContainer">
      <Button fluid color="blue" onClick={() => generateQRCode.refetch()} className="qr-login" loading={generateQRCode.isFetching}>
        {generateQRCode.data ? t("v8_generate_another_code", "Generate another code") : t("v8_login_with_qr_code", "Login with QR code")}
      </Button>
      <div className="qr-container mt-15">
        {renderLoader(isVerified, generateQRCode.isError, verifyQRCode.isError)}
        {renderQRCode(generateQRCode, verifyQRCode.isError, isExpired, isVerified)}
        {renderExpiration(isExpired, generateQRCode, verifyQRCode.isError, isVerified, secondsLeft, t)}
        {renderError(generateQRCode.isError, verifyQRCode.isError, t)}
      </div>
    </div>
  );
};
